/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';

import {
    blockMainActionVar,
    CreateTemplateInstanceWizardSteps,
    currentModeVar,
    currentStepVar,
    Mode,
    setTemplateInstance,
    templateInstanceIdVar,
} from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { LogCategory, ScenarioName, StatusName, StepName } from '../../../logger';
import {
    GetApprovalTemplateInstanceDetailsClient,
    GetApprovalTemplateInstanceDetailsScenario,
} from '../../../service/gen/templates';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { getStepNameByCurrentStep, getTemplateScenarioName } from '../../../utilities/TemplateUtils';
import { isSelfOwnedFormEnabled } from '../../../utilities/Utils';
import { ErrorPage, ErrorPageProps } from '../../containers/ErrorPage';
import { LoadingPage } from '../../containers/LoadingPage';
import { getInnerStackStyles, getStackStyles } from './CreateTemplateInstanceWizard.styles';
import { BasicSettings } from './steps/BasicSettings';
import { FormDesign } from './steps/FormDesign';
import { WorkflowSettings } from './steps/WorkflowDesign';

export const CreateTemplateInstanceWizard: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();

    const currentStep = useReactiveVar(currentStepVar);
    const templateInstanceId = useReactiveVar(templateInstanceIdVar);
    const currentMode = useReactiveVar(currentModeVar);

    const [isLoading, setIsLoading] = React.useState(true);
    const [errorProps, setErrorProps] = React.useState<ErrorPageProps>(null);

    const loadTemplateInstance = async (): Promise<void> => {
        setIsLoading(true);
        blockMainActionVar(true);
        const startTime = performance.now();

        try {
            const client = new GetApprovalTemplateInstanceDetailsClient();
            const result = isSelfOwnedFormEnabled()
                ? await client.getSelfOwnedApprovalTemplateInstanceDetails(
                      templateInstanceId,
                      GetApprovalTemplateInstanceDetailsScenario.Management
                  )
                : await client.getApprovalTemplateInstanceDetails(
                      templateInstanceId,
                      GetApprovalTemplateInstanceDetailsScenario.Management
                  );
            logger.logTraceInfo(
                LogCategory.TemplateInstanceWizard,
                'Get approval template instance detail successfully'
            );

            setTemplateInstance(result, Mode.Edit);
            setErrorProps(null);
            blockMainActionVar(false);

            logger.markScenario(ScenarioName.EditTemplateInstance, StepName.LoadTemplateInstance, StatusName.Success, {
                stepElapsed: performance.now() - startTime,
            });
        } catch (error) {
            logger.logTraceHttpException('GetApprovalTemplateInstanceDetails', error?.message, error?.status);
            logger.markScenario(ScenarioName.EditTemplateInstance, StepName.LoadTemplateInstance, StatusName.Fail, {
                stepElapsed: performance.now() - startTime,
            });

            setErrorProps({ onAction: loadTemplateInstance });
        } finally {
            setIsLoading(false);
        }
    };

    React.useEffect((): void => {
        logger.logTraceInfo(
            LogCategory.TemplateInstanceWizard,
            `Enter template instance wizard, current mode: ${currentMode === Mode.Create ? 'Create' : 'Edit'}`
        );

        const useEffectAsync = async (): Promise<void> => {
            blockMainActionVar(true);
            setIsLoading(true);

            const startTime = performance.now();

            try {
                if (currentMode === Mode.Edit) {
                    await loadTemplateInstance();
                }
            } catch (error) {
                let errorMsg;
                if (error?.status === 404) {
                    errorMsg = loc.strings.onboard_forms_wait_tip;
                }

                logger.logTraceHttpException('CheckUserInAdminTeam', error?.message, error?.status);
                logger.markScenario(
                    getTemplateScenarioName(currentMode),
                    StepName.CheckUserInAdminTeam,
                    StatusName.Fail,
                    {
                        stepElapsed: performance.now() - startTime,
                    }
                );
                setErrorProps({ onAction: useEffectAsync, message: errorMsg });
            } finally {
                setIsLoading(false);
            }
        };

        useEffectAsync();
    }, []);

    React.useEffect((): void => {
        const stepName = getStepNameByCurrentStep(currentStep);
        logger.markScenario(getTemplateScenarioName(currentMode), stepName, StatusName.NA);
    }, [currentStep]);

    return (
        <Stack className='create-template-instance-wizard-wrapper' horizontal styles={getStackStyles()}>
            {isLoading ? (
                <LoadingPage />
            ) : errorProps ? (
                <ErrorPage {...errorProps} />
            ) : (
                <>
                    <Stack
                        id='panel-1'
                        role='tab'
                        aria-expanded={currentStep === CreateTemplateInstanceWizardSteps.BasicSettings}
                        aria-labelledby='basic-settings-label'
                        aria-describedby='basic-settings-desc'
                        styles={getInnerStackStyles(currentStep === CreateTemplateInstanceWizardSteps.BasicSettings)}>
                        <BasicSettings />
                    </Stack>
                    <Stack
                        id='panel-2'
                        role='tab'
                        aria-labelledby='form-design-label'
                        aria-describedby='form-design-desc'
                        aria-expanded={currentStep === CreateTemplateInstanceWizardSteps.FormDesign}
                        styles={getInnerStackStyles(currentStep === CreateTemplateInstanceWizardSteps.FormDesign)}>
                        <FormDesign />
                    </Stack>
                    <Stack
                        id='panel-3'
                        role='tab'
                        aria-labelledby='workflow-settings-label'
                        aria-describedby='workflow-settings-desc'
                        aria-expanded={currentStep === CreateTemplateInstanceWizardSteps.WorkflowSettings}
                        styles={getInnerStackStyles(
                            currentStep === CreateTemplateInstanceWizardSteps.WorkflowSettings
                        )}>
                        <WorkflowSettings />
                    </Stack>
                </>
            )}
        </Stack>
    );
};
